import { useState, useRef } from "react";
import { ArrowUpRight, UploadCloud, CheckCircle2, AlertTriangle, FileText, QrCode } from "lucide-react";
import Reveal, { SectionLabel } from "../components/Reveal";
import PageHero from "../components/PageHero";
import { PAYMENT, ADMISSION_PROGRAMS } from "../lib/site";
import { useSettings } from "../lib/SettingsContext";

const API_BASE = process.env.REACT_APP_API_BASE || "";
const MAX_MB = 10;
const ACCEPT = ["image/jpeg", "image/png", "application/pdf"];

const Field = ({ label, name, type = "text", required, value, onChange, placeholder }) => (
  <div>
    <label className="section-label">{label}{required && " *"}</label>
    <input
      data-testid={`adm-${name}`}
      type={type}
      required={required}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(name, e.target.value)}
      className="mt-3 w-full bg-[#0a0a0a] border border-white/10 px-4 py-4 font-sans text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#d4af37] transition-colors"
    />
  </div>
);

const initial = {
  student_name: "", dob: "", age: "", gender: "", school_name: "", grade: "",
  parent_name: "", mobile: "", whatsapp: "", email: "", address: "",
  program: ADMISSION_PROGRAMS[0],
  hear_about: "", medical_needs: "", why_join: "",
};

export default function Admissions() {
  const settings = useSettings();
  const qrImage = settings.qr_image || PAYMENT.qrImage;
  const upiId = settings.upi_id || PAYMENT.upiId;
  const accountHolder = settings.account_holder || PAYMENT.accountHolder;
  const admissionFee = settings.admission_fee || "";
  const [d, setD] = useState(initial);
  const [file, setFile] = useState(null);
  const [declared, setDeclared] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);
  const fileRef = useRef();

  const set = (k, v) => setD((s) => ({ ...s, [k]: v }));

  const onFile = (f) => {
    setError("");
    if (!f) return;
    if (!ACCEPT.includes(f.type)) { setError("Please upload a JPG, PNG or PDF file."); return; }
    if (f.size > MAX_MB * 1024 * 1024) { setError("File is larger than 10 MB. Please upload a smaller file."); return; }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!file) { setError("Please upload your payment screenshot / receipt."); return; }
    if (!declared) { setError("Please confirm the declaration before submitting."); return; }
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(d).forEach(([k, v]) => fd.append(k, v));
      fd.append("declaration", "true");
      fd.append("receipt", file);
      const res = await fetch(`${API_BASE}/api/enroll.php`, { method: "POST", body: fd });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || "Submission failed. Please try again.");
      setSuccess(json);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(typeof err.message === "string" ? err.message : "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (success) {
    return (
      <>
        <PageHero label="Admissions" title={<>Application <em className="font-serif italic gold-text">received.</em></>} subtitle="" />
        <section data-testid="admission-success" className="py-24 lg:py-28">
          <div className="max-w-2xl mx-auto px-6 lg:px-10 text-center">
            <Reveal>
              <div className="mx-auto h-20 w-20 grid place-items-center rounded-full border border-[#d4af37]/50">
                <CheckCircle2 size={36} className="text-[#d4af37]" />
              </div>
              <h2 className="mt-9 font-serif text-4xl lg:text-5xl text-white font-light">Thank You!</h2>
              <p className="mt-6 font-sans text-base text-white/70 leading-relaxed">
                Your admission application has been submitted successfully.
              </p>
              <div className="mt-6 hairline p-5 inline-block">
                <div className="section-label">Your Application ID</div>
                <div data-testid="success-app-id" className="mt-2 font-serif text-2xl gold-text">{success.application_id}</div>
              </div>
              <p className="mt-7 font-sans text-sm text-white/60 leading-relaxed">
                Your admission will be confirmed <b className="text-white">only after we verify the payment received in our account.</b>
                <br />Our team will contact you within <b className="text-white">1–2 working days</b> on your registered mobile number or email.
              </p>
              <p className="mt-8 font-serif text-xl text-white/80 italic">Thank you for choosing Beyond The Stage Academy.</p>
            </Reveal>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <PageHero
        label="Admissions"
        title={<>Apply to <em className="font-serif italic gold-text">Beyond The Stage.</em></>}
        subtitle="Complete the application below, make the admission fee payment, and upload your receipt. Our team will verify and confirm your child's place."
      />

      <section data-testid="admission-form-section" className="py-20 lg:py-24">
        <form onSubmit={submit} className="max-w-4xl mx-auto px-6 lg:px-10 space-y-10">

          {/* STUDENT INFORMATION */}
          <Reveal>
            <div className="lux-card p-8 lg:p-10">
              <SectionLabel>Student Information</SectionLabel>
              <div className="mt-8 grid sm:grid-cols-2 gap-5">
                <Field label="Student Full Name" name="student_name" required value={d.student_name} onChange={set} />
                <Field label="Date of Birth" name="dob" type="date" required value={d.dob} onChange={set} />
                <Field label="Age" name="age" type="number" required value={d.age} onChange={set} />
                <div>
                  <label className="section-label">Gender</label>
                  <select data-testid="adm-gender" value={d.gender} onChange={(e) => set("gender", e.target.value)}
                    className="mt-3 w-full bg-[#0a0a0a] border border-white/10 px-4 py-4 font-sans text-sm text-white focus:outline-none focus:border-[#d4af37]">
                    <option value="">Select…</option>
                    <option>Female</option><option>Male</option><option>Prefer not to say</option>
                  </select>
                </div>
                <Field label="School Name" name="school_name" value={d.school_name} onChange={set} />
                <Field label="Current Grade / Class" name="grade" required value={d.grade} onChange={set} />
                <Field label="Parent / Guardian Name" name="parent_name" required value={d.parent_name} onChange={set} />
                <Field label="Mobile Number" name="mobile" type="tel" required value={d.mobile} onChange={set} />
                <Field label="WhatsApp Number" name="whatsapp" type="tel" value={d.whatsapp} onChange={set} />
                <Field label="Email Address" name="email" type="email" required value={d.email} onChange={set} />
                <div className="sm:col-span-2">
                  <label className="section-label">Residential Address</label>
                  <textarea data-testid="adm-address" rows={2} value={d.address} onChange={(e) => set("address", e.target.value)}
                    className="mt-3 w-full bg-[#0a0a0a] border border-white/10 px-4 py-4 font-sans text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#d4af37] resize-none" />
                </div>
              </div>
            </div>
          </Reveal>

          {/* PROGRAM */}
          <Reveal delay={0.05}>
            <div className="lux-card p-8 lg:p-10">
              <SectionLabel>Program Selection</SectionLabel>
              <div className="mt-8">
                <div>
                  <label className="section-label">Program *</label>
                  <select data-testid="adm-program" required value={d.program} onChange={(e) => set("program", e.target.value)}
                    className="mt-3 w-full bg-[#0a0a0a] border border-white/10 px-4 py-4 font-sans text-sm text-white focus:outline-none focus:border-[#d4af37]">
                    {ADMISSION_PROGRAMS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </Reveal>

          {/* ADDITIONAL INFO */}
          <Reveal delay={0.05}>
            <div className="lux-card p-8 lg:p-10">
              <SectionLabel>Additional Information</SectionLabel>
              <div className="mt-8 space-y-5">
                <div>
                  <label className="section-label">How did you hear about us?</label>
                  <input data-testid="adm-hear_about" value={d.hear_about} onChange={(e) => set("hear_about", e.target.value)}
                    className="mt-3 w-full bg-[#0a0a0a] border border-white/10 px-4 py-4 font-sans text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#d4af37]" />
                </div>
                <div>
                  <label className="section-label">Any special learning needs or medical information?</label>
                  <textarea data-testid="adm-medical_needs" rows={3} value={d.medical_needs} onChange={(e) => set("medical_needs", e.target.value)}
                    className="mt-3 w-full bg-[#0a0a0a] border border-white/10 px-4 py-4 font-sans text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#d4af37] resize-none" />
                </div>
                <div>
                  <label className="section-label">Why would you like to join Beyond The Stage Academy?</label>
                  <textarea data-testid="adm-why_join" rows={3} value={d.why_join} onChange={(e) => set("why_join", e.target.value)}
                    className="mt-3 w-full bg-[#0a0a0a] border border-white/10 px-4 py-4 font-sans text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#d4af37] resize-none" />
                </div>
              </div>
            </div>
          </Reveal>

          {/* PAYMENT */}
          <Reveal delay={0.05}>
            <div className="ivory-section p-8 lg:p-10 rounded-none">
              <SectionLabel>Admission Fee Payment</SectionLabel>

              {admissionFee && (
                <div data-testid="admission-fee" className="mt-6 font-serif text-2xl text-[#1a1a1a]">
                  Admission Fee: <span className="gold-text font-medium">{admissionFee}</span>
                </div>
              )}

              {/* Notice */}
              <div data-testid="payment-notice" className="mt-7 border border-[#d4af37]/50 bg-[#d4af37]/10 p-5 flex gap-4">
                <AlertTriangle size={22} className="text-[#b38728] shrink-0 mt-0.5" />
                <div className="font-sans text-sm leading-relaxed">
                  <b>Before making the payment, please mention the following in the Remarks / Message section of your UPI payment app:</b>
                  <div className="mt-2 font-serif text-lg">Student Name – Current Grade</div>
                  <div className="mt-1 text-[#5C5345]"><b>Example:</b> Kimaya Pandit – Grade 6</div>
                  <div className="mt-2 text-[#5C5345]">This helps us verify your payment quickly and match it with your admission form.</div>
                </div>
              </div>

              <div className="mt-8 grid sm:grid-cols-2 gap-8 items-center">
                <div className="flex justify-center">
                  {qrImage ? (
                    <img src={qrImage} alt="Payment QR Code" data-testid="payment-qr" className="w-64 max-w-full h-auto border border-black/10" />
                  ) : (
                    <div data-testid="payment-qr-placeholder" className="w-56 h-56 grid place-items-center bg-white/60 border-2 border-dashed border-black/20 text-center">
                      <div>
                        <QrCode size={40} className="mx-auto text-black/40" />
                        <div className="mt-3 font-sans text-xs text-black/50 px-4">QR Code will appear here</div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="space-y-5">
                  <div>
                    <div className="section-label">UPI ID</div>
                    <div data-testid="payment-upi" className="mt-2 font-serif text-xl">{upiId}</div>
                  </div>
                  <div>
                    <div className="section-label">Account Holder Name</div>
                    <div data-testid="payment-holder" className="mt-2 font-serif text-xl">{accountHolder}</div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          {/* UPLOAD */}
          <Reveal delay={0.05}>
            <div className="lux-card p-8 lg:p-10">
              <SectionLabel>Payment Confirmation</SectionLabel>
              <label className="section-label block mt-7">Upload Payment Screenshot / Receipt *</label>
              <div
                onClick={() => fileRef.current?.click()}
                data-testid="upload-dropzone"
                className="mt-4 cursor-pointer border-2 border-dashed border-white/15 hover:border-[#d4af37]/60 transition-colors p-8 text-center"
              >
                <input
                  ref={fileRef}
                  data-testid="adm-receipt"
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                  className="hidden"
                  onChange={(e) => onFile(e.target.files?.[0])}
                />
                {file ? (
                  <div className="flex items-center justify-center gap-3 text-white">
                    <FileText size={20} className="text-[#d4af37]" />
                    <span className="font-sans text-sm">{file.name}</span>
                    <span className="font-sans text-xs text-white/40">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                  </div>
                ) : (
                  <div className="text-white/50">
                    <UploadCloud size={30} className="mx-auto text-[#d4af37]" />
                    <div className="mt-3 font-sans text-sm">Click to upload — JPG, PNG or PDF</div>
                    <div className="mt-1 font-sans text-xs text-white/35">Maximum size 10 MB</div>
                  </div>
                )}
              </div>
            </div>
          </Reveal>

          {/* DECLARATION + SUBMIT */}
          <Reveal delay={0.05}>
            <div className="lux-card p-8 lg:p-10">
              <label className="flex items-start gap-4 cursor-pointer">
                <input
                  data-testid="adm-declaration"
                  type="checkbox"
                  checked={declared}
                  onChange={(e) => setDeclared(e.target.checked)}
                  className="mt-1 h-5 w-5 accent-[#d4af37]"
                />
                <span className="font-sans text-sm text-white/75 leading-relaxed">
                  I confirm that I have filled in the correct information and uploaded the payment receipt.
                </span>
              </label>

              {error && (
                <div data-testid="adm-error" className="mt-6 flex items-center gap-2 text-sm text-red-400">
                  <AlertTriangle size={16} /> {error}
                </div>
              )}

              <button
                data-testid="adm-submit"
                type="submit"
                disabled={sending}
                className="mt-8 w-full sm:w-auto btn-gold font-sans text-[11px] tracking-[0.3em] uppercase px-10 py-5 inline-flex items-center justify-center gap-3 disabled:opacity-60"
              >
                {sending ? "Submitting…" : "Submit Admission Application"}
                {!sending && <ArrowUpRight size={14} />}
              </button>
            </div>
          </Reveal>
        </form>
      </section>
    </>
  );
}
